import { useMemo } from "react";
import styled from "styled-components";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import type { AdminMrrStatsDB } from "@KassaroAB/shared/types/adminMrrStats";
import { formatMoney } from "../../utils/formatMoney";
import {
  getComparisonDate,
  getDatesInPeriod,
  getMoneyForCurrency,
  type SalesPeriodPreset,
} from "../../utils/dailySalesUtils";
import { LoadingContainer } from "../../components/LoadingContainer";
import { Card } from "../../components/Card";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";

const ChartCard = styled(Card)`
  padding: ${spacing.x2}px;
  margin-bottom: ${spacing.x3}px;
`;

const ChartTitle = styled.h3`
  margin: 0 0 ${spacing.x2}px 0;
  font-size: ${fontSizes.large}px;
`;

const CHART_CURRENCY = Currency.SEK;

const getAmountByDate = (stats: AdminMrrStatsDB[] | undefined) =>
  new Map(
    (stats ?? []).map((day) => [
      day.date,
      getMoneyForCurrency(day.mrrByCurrency, CHART_CURRENCY)?.amount ?? 0,
    ])
  );

interface MrrChartProps {
  period: { from: string; to: string };
  periodPreset: SalesPeriodPreset;
  stats: AdminMrrStatsDB[] | undefined;
  comparisonStats: AdminMrrStatsDB[] | undefined;
  isLoading: boolean;
  onSelectDate: (date: string) => void;
}

export const MrrChart = ({
  period,
  periodPreset,
  stats,
  comparisonStats,
  isLoading,
  onSelectDate,
}: MrrChartProps) => {
  const { t } = useTranslation();

  const chartData = useMemo(() => {
    const current = getAmountByDate(stats);
    const previous = getAmountByDate(comparisonStats);
    return getDatesInPeriod(period.from, period.to).map((date) => {
      const previousDate = getComparisonDate(date, periodPreset, period);
      return {
        id: date,
        name: DateTime.fromISO(date).toFormat("d MMM"),
        previousName: DateTime.fromISO(previousDate).toFormat("d MMM"),
        current: current.get(date) ?? null,
        previous: previous.get(previousDate) ?? null,
      };
    });
  }, [stats, comparisonStats, period, periodPreset]);

  if (isLoading) {
    return (
      <ChartCard>
        <LoadingContainer message={t("statistics.loading")} />
      </ChartCard>
    );
  }

  const formatAmount = (amount: number) =>
    formatMoney({ amount, currency: CHART_CURRENCY });

  return (
    <ChartCard>
      <ChartTitle>
        {t("adminMrrStats.mrrPerDay", { currency: CHART_CURRENCY })}
      </ChartTitle>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart
          data={chartData}
          margin={{ top: spacing.x2, right: spacing.x2, left: 0, bottom: 0 }}
          onClick={(state) => {
            const id = state?.activePayload?.[0]?.payload?.id;
            if (typeof id === "string") {
              onSelectDate(id);
            }
          }}
        >
          <CartesianGrid stroke={colors.border} vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: fontSizes.small }} />
          <YAxis
            tickFormatter={formatAmount}
            tick={{ fontSize: fontSizes.small }}
            width={90}
          />
          <Legend
            wrapperStyle={{ fontSize: fontSizes.small }}
          />
          <Tooltip
            formatter={(value: number, _name: string, item) => [
              formatAmount(value),
              item.dataKey === "current"
                ? item.payload.name
                : (item.payload.previousName ?? ""),
            ]}
            labelFormatter={() => ""}
            cursor={{ stroke: colors.border, strokeWidth: 1 }}
          />
          <Line
            type="monotone"
            dataKey="previous"
            name={t("adminMrrStats.previousPeriod")}
            stroke={colors.textMuted}
            strokeDasharray="4 4"
            strokeWidth={2}
            dot={false}
            connectNulls
          />
          <Line
            type="monotone"
            dataKey="current"
            name={t("adminMrrStats.currentPeriod")}
            stroke={colors.primary}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 5, cursor: "pointer" }}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};
